// @givmo/app-admin — Giving › Match Program (EP-MNT2; D-CPO-34; OM-17 design 5; D-EP-48 ruling 2).
//
// The employer match program as a PAGE, not a settings tab. It fails the knob test three ways — the
// policy is a published text employees read, the budget is consumed through the year and reported,
// and requests are worked through approvals — so it left Settings for its own row under Giving. The
// page composes shipped parts:
//   - the policy builder + the rendered Match Policy with Copy — `MatchPolicySection`, the Settings
//     tab moved VERBATIM (its `settings-match-*` testids included; see that file's header);
//   - a link to Approvals — the work surface stays `/approvals`.
//
// The year's utilization is NOT repeated here: the EP-WEB9 card stays the dashboard's alone (the
// EP-MNT2 mockup ruling, 2026-09-29), and that card's "Set a budget" door points here.
//
// ★ NEVER OVERWRITE: the writer is unchanged. The builder PUTs `/admin/company/match_policy` with the
// body `buildMatchPolicyBody` builds for the onboarding wizard too, byte-identical (pinned in this
// page's suite). `/settings?tab=match` redirects here at the route.
//
// The page lives in the employer module package (EP-MNT3) and becomes `/donations/match-program` at
// the console mount (EP-MNT4).

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { companiesGetCompanyOptions } from '@givmo/api-client/query';
import { isGivmoError, supportReference } from '@givmo/api-client';
import { Icon, PageHeader, Skeleton, testIdProp } from '@givmo/design-system';
import { ArrowRight } from 'lucide-react';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { MatchPolicySection } from './match-policy-builder';

const PREFIX = 'match-program';

export function MatchProgramPage() {
  const paths = useEmployerModulePaths();
  const companyQuery = useQuery(companiesGetCompanyOptions());
  const company = companyQuery.data;

  return (
    <div
      className="mx-auto flex w-full max-w-[1080px] flex-col gap-20 px-24 py-24"
      {...testIdProp('page-match-program')}
    >
      <PageHeader
        title="Match Program"
        subtitle="The policy your employees see, this year’s budget, and where their requests are approved."
        actions={
          <Link
            to={paths.approvals}
            className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
            {...testIdProp(`${PREFIX}-approvals-link`)}
          >
            Review requests in Approvals
            <Icon icon={ArrowRight} size={14} aria-hidden />
          </Link>
        }
      />

      {company !== undefined ? (
        <MatchPolicySection company={company} />
      ) : companyQuery.isError ? (
        <div className="flex flex-col items-start gap-4" {...testIdProp(`${PREFIX}-error`)}>
          <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
            {isGivmoError(companyQuery.error)
              ? companyQuery.error.message
              : 'Failed to load your match program.'}
          </p>
          {isGivmoError(companyQuery.error) && supportReference(companyQuery.error) && (
            <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
              {supportReference(companyQuery.error)}
            </p>
          )}
          <button
            type="button"
            onClick={() => void companyQuery.refetch()}
            className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
            {...testIdProp(`${PREFIX}-error-retry`)}
          >
            Try again
          </button>
        </div>
      ) : (
        <div {...testIdProp(`${PREFIX}-loading`)}>
          <Skeleton className="h-12 w-1/3" />
          <Skeleton className="mt-12 h-12 w-full" />
          <Skeleton className="mt-12 h-12 w-2/3" />
        </div>
      )}
    </div>
  );
}
